import { v4 as uuid } from "uuid"

import getVariantsByVariantId from "@secureo/common/reducers/productDetailPageReducer/utils/getVariantsByVariantId"
import {
	getInitialProductId,
	getInitialVariantId,
} from "@secureo/common/reducers/productDetailPageReducer/utils/getInitialReducerState"
import getMergedAttributes from "@secureo/common/reducers/productDetailPageReducer/utils/getMergedAttributes"
import getInitialCylinderConfigurations from "@secureo/common/reducers/productDetailPageReducer/utils/getInitialCylinderConfigurations"
import getPricePortions from "@secureo/common/reducers/productDetailPageReducer/utils/getPricePortions"
import getMsrpPricePortions from "@secureo/common/reducers/productDetailPageReducer/utils/getMsrpPricePortions"

import {
	LockingSystemProduct,
	LockingSystemComponentType,
	LockingSystemComponent,
} from "@secureo/common/typings/LockingSystem"
import { CountryCode } from "@secureo/common/typings/CountryCode"

const getLockingSystemComponentFromLockingSystemProduct = (
	lockingSystemProduct: LockingSystemProduct,
	type: LockingSystemComponentType,
	countryCode: CountryCode,
	taxRate: number,
	quantity = 1,
	customComponentName = "",
	initialUuid?: string,
) => {
	const {
		variants,
		initialCylinderConfigurationVariant = null,
		initialConfigurationsByConfigurationGroup = {},
		shortDescription,
		sku: masterVariantSku,
	} = lockingSystemProduct

	const sku = initialCylinderConfigurationVariant?.sku ?? masterVariantSku
	const masterVariant = variants?.[0] ?? null
	const { lockingSystemComponentName } = masterVariant?.attributes ?? null

	const images = masterVariant.images ?? []

	const variantsByVariantId = getVariantsByVariantId(variants)
	const initialProductId = getInitialProductId(
		lockingSystemProduct.id,
		initialCylinderConfigurationVariant,
	)
	const initialVariantId = getInitialVariantId(variants)

	const attributes = getMergedAttributes(
		variantsByVariantId,
		initialVariantId,
		initialCylinderConfigurationVariant,
	)

	const { cylinderConfigurationType, selectedSystem, selectedOuterLength, selectedInnerLength } =
		getInitialCylinderConfigurations(attributes)

	const {
		singleNetPriceCents,
		singleGrossPriceCents,
		totalNetPriceCents,
		totalGrossPriceCents,
		pricePortions,
	} = getPricePortions(
		variantsByVariantId,
		initialVariantId,
		taxRate,
		quantity,
		countryCode,
		{},
		initialCylinderConfigurationVariant,
	)

	const {
		singleNetMsrpPriceCents,
		singleGrossMsrpPriceCents,
		totalNetMsrpPriceCents,
		totalGrossMsrpPriceCents,
		msrpPricePortions,
	} = getMsrpPricePortions(quantity, taxRate, countryCode, attributes, {})

	const lockingSystemComponent: LockingSystemComponent = {
		uuid: initialUuid || uuid(),

		// Product
		productId: initialProductId,
		variantId: initialVariantId,
		sku,
		masterVariantSku,
		attributes,
		variantsByVariantId,
		images,
		shortDescription,

		// Component
		type,
		componentName: lockingSystemComponentName,
		name: customComponentName,

		// Price
		quantity,
		singleNetPriceCents,
		singleGrossPriceCents,
		totalNetPriceCents,
		totalGrossPriceCents,
		pricePortions: {
			productSingleNetPriceCents: pricePortions.productSingleNetPriceCents,
			productSingleGrossPriceCents: pricePortions.productSingleGrossPriceCents,
			totalConfigurationsNetPriceCentsPerProductQuantity:
				pricePortions.totalConfigurationsNetPriceCentsPerProductQuantity,
			totalConfigurationsGrossPriceCentsPerProductQuantity:
				pricePortions.totalConfigurationsGrossPriceCentsPerProductQuantity,
		},

		// Msrp price
		singleNetMsrpPriceCents,
		singleGrossMsrpPriceCents,
		totalNetMsrpPriceCents,
		totalGrossMsrpPriceCents,
		msrpPricePortions: {
			productSingleNetPriceCents: msrpPricePortions.productSingleNetPriceCents,
			productSingleGrossPriceCents: msrpPricePortions.productSingleGrossPriceCents,
			totalConfigurationsNetPriceCentsPerProductQuantity:
				msrpPricePortions.totalConfigurationsNetPriceCentsPerProductQuantity,
			totalConfigurationsGrossPriceCentsPerProductQuantity:
				msrpPricePortions.totalConfigurationsGrossPriceCentsPerProductQuantity,
		},

		// Configurations
		configurationsByConfigurationGroup: initialConfigurationsByConfigurationGroup,

		// Cylinders
		cylinderConfigurationVariant: initialCylinderConfigurationVariant,
		cylinderConfigurationType,
		selectedSystem,
		selectedOuterLength,
		selectedInnerLength,
	}

	return lockingSystemComponent
}

export default getLockingSystemComponentFromLockingSystemProduct
